import { FieldItemDto, ServizioDto } from "@api/client";
import { serviceUpdateService } from "common/_redux/service/actions";
import { serviceServiceSelector } from "common/_redux/service/selectors";
import BftButton from "common/components/button";
import BftInfoTooltip from "common/components/info-tooltip";
import {
	DynamicValidators,
	DynamicValidatorsFields,
} from "common/validators/dynamic-validators.utils";
import { AppRouterService } from "core/routes/app-router.service";
import { PathEnum } from "core/routes/path.enum";
import { useAppDispatch, useAppSelector } from "core/store/hooks";
import { Formik } from "formik";
import { layoutStepsSelector } from "layout/_redux/selectors";
import { useEffect, useState } from "react";
import { Form } from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";

interface InitialValues {
	numeroContiRichiesti: string | undefined;
	motivazioneAperturaConto: { [key: string]: boolean } | undefined;
}

const ContoDiPagamento: React.FC = () => {
	const dispatch = useAppDispatch();
	const navigate = useNavigate();

	const location = useLocation();

	const steps = useAppSelector(layoutStepsSelector);
	const service = useAppSelector(serviceServiceSelector);

	const [motivazioniSorted, setMotivazioniSorted] = useState<FieldItemDto[]>([]);

	const getMotivazioniInitalValues = () => {
		var res = {};

		service?.motivazioneAperturaConto?.items?.forEach((m) => {
			Object.assign(res, {
				[m?.name as string]: m?.value == "true",
			});
		});

		return res;
	};

	const getValidationSchema = () => {
		let obj: DynamicValidatorsFields = {
			numeroContiRichiesti: service?.numeroContiRichiesti,
			motivazioneAperturaConto: service?.motivazioneAperturaConto,
		};

		return DynamicValidators.getSchema(obj);
	};

	const handleSubmit = (values: InitialValues) => {
		var request: ServizioDto = {
			...service,

			numeroContiRichiesti: service?.numeroContiRichiesti?.visible
				? {
						...service?.numeroContiRichiesti,
						value: values?.numeroContiRichiesti,
				  }
				: service?.numeroContiRichiesti,
			motivazioneAperturaConto: {
				...service?.motivazioneAperturaConto,
				items: service?.motivazioneAperturaConto?.items?.map((item) => {
					if (!item?.visible) return item;

					return {
						...item,
						value: String(values?.motivazioneAperturaConto?.[item?.name!]),
					};
				}),
			},
		};

		dispatch(serviceUpdateService(request))
			.unwrap()
			.then((res) => {
				if (res) {
					let substeps = steps?.find(
						(s) => s.route == AppRouterService.get(PathEnum.step4)
					)?.subSteps;

					let currentSubstepsIndex = substeps?.findIndex((ss) => ss.route == location.pathname);

					if (currentSubstepsIndex == undefined) {
						console.error("No current substep found");
						return;
					}

					let nextStep = substeps?.[currentSubstepsIndex + 1]?.route;

					if (!nextStep) {
						console.error("No next step found");
						return;
					}

					navigate(nextStep);
				}
			});
	};

	useEffect(() => {
		if (service && service?.motivazioneAperturaConto?.items) {
			let _motivazioni = [...service?.motivazioneAperturaConto?.items];

			_motivazioni = _motivazioni.sort((a, b) => {
				if (a?.order && b?.order) return a.order - b.order;

				return -1;
			});

			setMotivazioniSorted(_motivazioni);
		}
	}, [service]);

	return (
		<Formik
			initialValues={
				{
					numeroContiRichiesti:
						service?.numeroContiRichiesti?.value != "0"
							? service?.numeroContiRichiesti?.value
							: undefined,
					motivazioneAperturaConto: getMotivazioniInitalValues() || undefined,
				} as InitialValues
			}
			onSubmit={handleSubmit}
			validationSchema={getValidationSchema}
			enableReinitialize
		>
			{({ values, errors, touched, handleSubmit: _handleSubmit, handleChange }) => {
				return (
					<form
						onSubmit={(e) => {
							// console.log("errors", errors);
							_handleSubmit(e);
						}}
					>
						{service?.numeroContiRichiesti?.visible && (
							<>
								<h5 className="container mb-3 mt-2">Numero di conti richesti</h5>
								<div className="container d-flex">
									<BftButton
										type="button"
										className={`${
											values?.numeroContiRichiesti === "1"
												? "bg-primary border-0 text-white col-2"
												: "bg-light text-dark col-2 border-0"
										}`}
										onClick={() => handleChange("numeroContiRichiesti")("1")}
									>
										1
									</BftButton>

									<BftButton
										type="button"
										className={`${
											values?.numeroContiRichiesti === "2"
												? "bg-primary border-0 text-white col-2"
												: "bg-light text-dark col-2 border-0"
										}`}
										onClick={() => handleChange("numeroContiRichiesti")("2")}
									>
										2
									</BftButton>

									<BftButton
										type="button"
										className={`${
											values?.numeroContiRichiesti === "3"
												? "bg-primary border-0 text-white col-2"
												: "bg-light text-dark col-2 border-0"
										}`}
										onClick={() => handleChange("numeroContiRichiesti")("3")}
									>
										3
									</BftButton>
								</div>
								{errors.numeroContiRichiesti && (
									<Form.Control.Feedback className="ms-3 mt-0" type="invalid">
										{errors.numeroContiRichiesti}
									</Form.Control.Feedback>
								)}
							</>
						)}

						{motivazioniSorted?.some((m) => m.visible) && (
							<>
								<h5 className="container mb-3 mt-2">Motivo di apertura del Conto di Pagamento</h5>

								{motivazioniSorted?.map((motivazione, i) => {
									if (!motivazione?.visible) return null;

									return (
										<div
											key={`motivazione-apertura-conto_${i}`}
											className="bg-light d-flex justify-content-between align-items-center col-12 rounded mt-2"
										>
											<div className="container text-left rounded">
												<Form.Check
													type="checkbox"
													className="text-left rounded"
													label={motivazione?.label}
													name={`motivazioneAperturaConto.${motivazione?.name}`}
													checked={values?.motivazioneAperturaConto?.[motivazione?.name!]}
													onChange={handleChange}
													disabled={motivazione?.readonly}
													isInvalid={
														!!(
															errors?.motivazioneAperturaConto?.[
																// @ts-ignore
																motivazione?.tipoMotivazioneAperturaConto!
															] &&
															// @ts-ignore
															touched?.motivazioneAperturaConto?.[
																// @ts-ignore
																motivazione?.tipoMotivazioneAperturaConto!
															]
														)
													}
													feedback={
														// @ts-ignore
														touched?.motivazioneAperturaConto?.[
															motivazione?.name!
															// @ts-ignore
														] &&
														errors?.motivazioneAperturaConto?.[
															// @ts-ignore
															motivazione?.name!
														]
															? errors?.motivazioneAperturaConto[
																	// @ts-ignore
																	motivazione?.name
															  ]
															: null
													}
													feedbackType="invalid"
												/>
											</div>
											<div>
												<BftInfoTooltip className="me-4" message={motivazione?.tooltip} />
											</div>
										</div>
									);
								})}
							</>
						)}

						<div className="mt-3">
							<BftButton type="submit" className="col-12 text-bg-dark border-0">
								Avanti
							</BftButton>
						</div>
					</form>
				);
			}}
		</Formik>
	);
};
export default ContoDiPagamento;
